import { useState, useContext } from "react";
import {
  getProjects,
  postProject,
  getProjectDetails,
  updateProjectDetails,
  getProjectUsers,
  uploadProjectUsers,
  deleteProjectUser,
  updateProjectUser,
  getProjectFiles,
  uploadProjectFile,
  deleteProjectFile,
  getProjectSites,
  bulkDeleteProjectUsers,
} from "../../../Services/projectServices";
import toast from "react-hot-toast";
import { TOAST_MESSAGES } from "../Components/constants";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import { UserContext } from "../../../Context";
import { PROJECT_FILTER_TYPES_VALUES } from "../constant";

export const useProjects = ({ projectId = null } = {}) => {
  const { user } = useContext(UserContext);
  const userRole = user?.role;

  // common projects state
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    type: "",
    status: "",
    category: "",
    search: "",
    project: PROJECT_FILTER_TYPES_VALUES[userRole] || "all",
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10); // default rows per page

  // details & users
  const [projectDetails, setProjectDetails] = useState(null);
  const [projectUsers, setProjectUsers] = useState([]);
  const [totalUserCount, setTotalUserCount] = useState(0);

  // files
  const [projectFiles, setProjectFiles] = useState([]);
  const [totalProjectFiles, setTotalProjectFiles] = useState(0);

  // sites
  const [projectSites, setProjectSites] = useState([]);
  const [totalProjectSites, setTotalProjectSites] = useState(0);

  const [failedUsers, setFailedUsers] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await getProjects({
        ...filters,
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
      });
      if (res.status === 200) {
        setProjects(res.data.results);
        setTotalPages(Math.max(1, Math.ceil(res.data.count / rowsPerPage)));
        setError(null);
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.PROJECT_FETCH_FAILED;
        toast.error(msg);
      }
    } catch (err) {
      setError(TOAST_MESSAGES.PROJECT_FETCH_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = (newPage) => setPage(newPage);

  const onPageSizeChange = (newSize) => {
    setRowsPerPage(newSize);
    setPage(1); // reset to first page when page size changes
  };

  const createProject = async (data) => {
    setLoading(true);
    try {
      const res = await postProject(data);
      if (res.status === 201) {
        setProjects((prev) => [res.data, ...prev]);
        toast.success(TOAST_MESSAGES.PROJECT_CREATED);
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.PROJECT_CREATION_ERROR;
        toast.error(msg);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.PROJECT_CREATION_ERROR;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectDetails = async (params) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await getProjectDetails(projectId, params);
      if (res.status === 200) {
        setProjectDetails(res.data);
        setError(null);
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.PROJECT_DETAILS_FAILED;
        toast.error(msg);
      }
    } catch (err) {
      setError(TOAST_MESSAGES.PROJECT_DETAILS_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const saveProjectDetails = async (data) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await updateProjectDetails(projectId, data);
      if (res.status === 200) {
        setProjectDetails(res.data);
        toast.success(TOAST_MESSAGES.UPDATE_PROJECT_DETAILS_SUCCESS);
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) ||
          TOAST_MESSAGES.UPDATE_PROJECT_DETAILS_FAILED;
        toast.error(msg);
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.UPDATE_PROJECT_DETAILS_ERROR;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectUsers = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const offset = (page - 1) * rowsPerPage;
      const res = await getProjectUsers(projectId, {
        limit: rowsPerPage,
        offset,
        search: filters.search,
      });
      if (res.status === 200) {
        const total = Math.ceil((res.data?.count || 0) / rowsPerPage);
        setProjectUsers(res.data.results);
        setTotalPages(Math.max(1, Math.ceil(res.data.count / rowsPerPage)));
        setTotalUserCount(res.data.count || 0);

        // if current page > total pages, go back
        if (page > total && total > 0) {
          setPage(total);
        } else if (total === 0) {
          setPage(1);
        }
      } else {
        setProjectUsers([]);
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.USERS_FETCH_FAILED;
        toast.error(msg);
      }
    } catch (err) {
      setProjectUsers([]);
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.USERS_FETCH_ERROR;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const uploadUsersFile = async (file) => {
    if (!projectId) return;
    try {
      const res = await uploadProjectUsers(projectId, file);
      if (res.status === 201) {
        toast.success(TOAST_MESSAGES.USERS_UPLOAD_SUCCESS);
      } else if (res.status === 207) {
        setFailedUsers(res.data.skipped_rows || []);
        setTotalRows(res.data.total_rows);
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.USERS_UPLOAD_PARTIAL;
        toast.error(msg);
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.USERS_UPLOAD_FAILED;
        toast.error(msg);
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.USERS_UPLOAD_ERROR;
      toast.error(msg);
    } finally {
      fetchProjectUsers();
    }
  };

  const updateUser = async (userId, role) => {
    if (!projectId) return false;
    try {
      const res = await updateProjectUser(projectId, userId, { role });
      if (res.status === 200) {
        toast.success(TOAST_MESSAGES.USER_UPDATION_SUCCESS);
        fetchProjectUsers();
        return true;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.USER_UPDATION_FAILED;
        toast.error(msg);
        return false;
      }
    } catch (err) {
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.USER_UPDATION_ERROR;
      toast.error(msg);
      return false;
    }
  };

  const deleteUser = async (userId) => {
    if (!projectId) return false;
    try {
      const res = await deleteProjectUser(projectId, userId);
      if (res.status === 204) {
        toast.success(TOAST_MESSAGES.USER_DELETE_SUCCESS);
        fetchProjectUsers();
        return true;
      } else {
        toast.error(TOAST_MESSAGES.USER_DELETE_FAILED);
        return false;
      }
    } catch (err) {
      toast.error(TOAST_MESSAGES.USER_DELETE_ERROR);
      return false;
    }
  };

  const fetchProjectFiles = async () => {
    if (!projectId) return;
    setLoading(true);

    try {
      const res = await getProjectFiles(projectId, {
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
      });

      if (res.status === 200) {
        setProjectFiles(res.data.results || []);
        setTotalProjectFiles(res.data.count || 0);
        setTotalPages(Math.max(1, Math.ceil(res.data.count / rowsPerPage)));
      } else {
        toast.error(
          extractErrorMessage(res) || TOAST_MESSAGES.FILES_FETCH_FAILED
        );
      }
    } catch (err) {
      toast.error(
        extractErrorMessage(err) || TOAST_MESSAGES.FILES_FETCH_FAILED
      );
    } finally {
      setLoading(false);
    }
  };

  const uploadProjectFileHandler = async (file) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await uploadProjectFile(projectId, formData);
      if (res.status === 201) {
        toast.success(TOAST_MESSAGES.FILE_UPLOAD_SUCCESS);
      } else {
        toast.error(
          extractErrorMessage(res) || TOAST_MESSAGES.FILE_UPLOAD_FAILED
        );
      }
    } catch (err) {
      toast.error(
        extractErrorMessage(err) || TOAST_MESSAGES.FILE_UPLOAD_FAILED
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteProjectFileHandler = async (fileId) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await deleteProjectFile(projectId, fileId);
      if (res.status === 204 || res.status === 200) {
        setProjectFiles((prev) => prev.filter((file) => file.id !== fileId)); // update files list
        setTotalProjectFiles((prev) => Math.max(0, prev - 1)); // decrementing total count
        toast.success(TOAST_MESSAGES.FILE_DELETE_SUCCESS);
      } else {
        toast.error(
          extractErrorMessage(res) || TOAST_MESSAGES.FILE_DELETE_FAILED
        );
      }
    } catch (err) {
      toast.error(
        extractErrorMessage(err) || TOAST_MESSAGES.FILE_DELETE_FAILED
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectSites = async (params) => {
    if (!projectId) return;
    setLoading(true);
    try {
      const offset = (page - 1) * rowsPerPage;
      const res = await getProjectSites(projectId, {
        limit: rowsPerPage,
        offset,
        ...params,
      });
      if (res.status === 200) {
        setProjectSites(res.data.results);
        setTotalProjectSites(res.data.count);
        setTotalPages(Math.ceil(res.data.count / rowsPerPage));
      } else {
        setProjectSites([]);
        setTotalProjectSites(0);
      }
    } catch (err) {
      setProjectSites([]);
      setTotalProjectSites(0);
      const msg =
        extractErrorMessage(err) || TOAST_MESSAGES.FETCHING_SITES_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const bulkDeleteUsers = async (userIds) => {
    if (!projectId) return false;
    try {
      const res = await bulkDeleteProjectUsers(projectId, userIds);
      if (res.status === 200) {
        toast.success(TOAST_MESSAGES.USERS_DELETE_SUCCESS);
        fetchProjectUsers(); // refresh after deletion
        return true;
      } else if (res.status === 207) {
        toast.success(TOAST_MESSAGES.USERS_DELETE_PARTIAL);
        fetchProjectUsers();
        return false;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.USERS_DELETE_FAILED;
        toast.error(msg);
        return false;
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || TOAST_MESSAGES.USERS_DELETE_ERROR;
      toast.error(msg);
      return false;
    }
  };

  return {
    // projects
    projects,
    fetchProjects,
    createProject,

    // loading/error
    loading,
    error,

    // filters & pagination
    setFilters,
    filters,
    page,
    setPage,
    totalPages,
    rowsPerPage,
    onPageChange,
    onPageSizeChange,

    // details
    projectDetails,
    fetchProjectDetails,
    saveProjectDetails,

    // users
    projectUsers,
    fetchProjectUsers,
    uploadUsersFile,
    updateUser,
    deleteUser,
    bulkDeleteUsers,
    totalUserCount,

    // files
    projectFiles,
    totalProjectFiles,
    fetchProjectFiles,
    uploadProjectFileHandler,
    deleteProjectFileHandler,

    // sites
    projectSites,
    totalProjectSites,
    fetchProjectSites,

    failedUsers,
    setFailedUsers,
    totalRows,
  };
};
